import PropTypes from 'prop-types';
import {
  MenuItem, ListItemIcon, ListItemText,
  CircularProgress, ButtonGroup, ListItem,
  Collapse, List, makeStyles,
  Tooltip,
} from '@material-ui/core';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import AccessAlarmIcon from '@material-ui/icons/AccessAlarm';
import EmailIcon from '@material-ui/icons/Email';
import PersonIcon from '@material-ui/icons/Person';
import { ArrowDropDown } from '@material-ui/icons';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import tw from 'twin.macro';
import { useAnalytics } from 'use-analytics';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import {
  SearchStatus, BookingAction, UserRolePermissions, TravelTypes,
  SearchModes,
  TravelPolicyActions,
} from '../../../../constants';
import { checkSitePermission } from '../../../../store/slices/user';
import {
  saveTripRequest, saveSendTripEmailRequest, setItineraryOnHold,
} from '../../../../store/slices/trip';
import checkItineraryCanHold from '../../../../helpers/checkItineraryCanHold';
import getTripItineraries from '../../../../helpers/getTripItineraries';
import getRateOptionRulesKey from '../../../../helpers/getRateOptionRulesKey';
import formatBookButtonTextByModes from '../../../../helpers/formatters/formatBookButtonTextByModes';
import formatBookButtonTextByTrip from '../../../../helpers/formatters/formatBookButtonTextByTrip';
import ContactAdvisorForm from './ContactAdvisorForm';
import Popover from '../../../Popover';
import EmailTripQuoteForm from './EmailTripQuoteForm';
import getHighestRankedCabinFlightItinerary from '../../../../helpers/flightCabins/getHighestRankedCabinFlightItinerary';
import LowerCabinAlertModal from './LowerCabinAlertModal';
import flightSearchInput from '../../../../helpers/selectors/flightSearchInput';
import getPreferredCabins from '../../../../helpers/flightCabins/getPreferredCabins';
import getPreferredCabinByFlightSegmentIndex from '../../../../helpers/flightCabins/getPreferredCabinByFlightSegmentIndex';
import checkDoFlightHaveCabinDowngrade from '../../../../helpers/flightCabins/checkDoFlightHaveCabinDowngrade';
import checkTravelPolicies from '../../../../helpers/checkTravelPolicies';
import TravelPolicyConfirmationModal from '../../TravelPolicyConfirmationModal';

const useButtonGroupStyles = makeStyles(() => ({
  root: tw`w-full`,
  grouped: (buttonGroupStyles = {}) => ({ ...buttonGroupStyles }),
}));

const useListItemStyles = makeStyles(() => ({ root: { whiteSpace: 'normal' } }));
const useListItemTextStyles = makeStyles(() => ({
  listItemText: {
    fontFamily: 'var(--fontFamilyBody)',
  },
}));

const useStyles = makeStyles((theme) => ({
  arrow: {
    color: theme.palette.common.white,
  },
  tooltip: {
    fontFamily: 'var(--fontFamilyBody)',
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    borderStyle: 'solid',
    borderWidth: '2px',
    fontSize: '12px',
  },
}));

const TripBookButton = ({
  tripId, trip, pricing, saveTripStatus, error, saveSendTripEmailStatus,
  buttonGroupStyles, buttonColorCssClass, disableBookingTooltip,
  onBookClick, saveSendTripEmailError, disabled,
  tripRoomSearchInput,
}) => {
  const router = useRouter();
  const { track } = useAnalytics();
  const dispatch = useDispatch();
  const classes = useStyles();
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const canSendEmailQuote = useSelector(checkSitePermission(UserRolePermissions.canSendEmailQuote));
  const canRequestAdvisor = useSelector(checkSitePermission(UserRolePermissions.canRequestAdvisor));
  const { id } = router.query;
  const brochure = useSelector((state) => state.brochure.brochures[id]);
  const canBook = useSelector(checkSitePermission(UserRolePermissions.canBook));
  const canHold = useSelector(checkSitePermission(UserRolePermissions.canHold));
  const { selectedSearchMode } = useSelector((store) => store.tripSearch);
  const { searchObj } = useSelector(flightSearchInput(tripId,
    selectedSearchMode === SearchModes.FLIGHT || !tripId));

  const listItemClasses = useListItemStyles();
  const listItemTextClasses = useListItemTextStyles();
  const buttonGroupClasses = useButtonGroupStyles(buttonGroupStyles);
  const [doOpenEmailInput, setOpenEmailInput] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openContactAdvInput, setOpenContactAdvInput] = useState(false);
  const [doShowMixedCabinWarning, setDoShowMixedCabinWarning] = useState(false);
  const allRateOptionRules = useSelector((store) => store.trip.rateOptionRules);
  const { travelAgency } = useSelector((store) => store.user);

  const policies = travelAgency && travelAgency.travelPolicies;

  const [policyStatus, setPolicyStatus] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (policies && policies.length > 0) {
      const result = checkTravelPolicies(trip, policies);
      setPolicyStatus(result);
    }
  }, [trip, policies]);

  const [travelPolicyModal, setTravelPolicyModal] = useState({
    isOpen: false,
    title: '',
    content: '',
    okButtonLabel: '',
    onConfirmClick: () => {},
    action: -1,
  });

  const handleCloseTravelPolicyModal = () => {
    setTravelPolicyModal((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  };

  const menuItems = [];
  if (canSendEmailQuote) menuItems.push(UserRolePermissions.canSendEmailQuote);
  if (canRequestAdvisor) menuItems.push(UserRolePermissions.canRequestAdvisor);
  if (canBook
    || (isBrochurePage && brochure?.doAllowBook)) menuItems.push(UserRolePermissions.canBook);
  if (canHold) menuItems.push(UserRolePermissions.canHold);

  const getTravelModesForHoldButton = useCallback(
    () => {
      const itineraries = getTripItineraries(trip);
      if (itineraries.length === 0) {
        return {
          itinerariesOnHold: [], travelTypesOnHold: [], travelTypesToBook: [],
        };
      }

      const itinerariesOnHold = _.filter(itineraries, (it) => checkItineraryCanHold(it)
        && (it.mode !== TravelTypes.flight
          || (!(trip.hotelItineraries && (Object.keys(trip.hotelItineraries).length > 0))
          && !(trip.rentalCarItineraries
            && _.find(trip.rentalCarItineraries, (r) => r.priceInfo.paymentTypeCode !== null)))));
      const itinerariesModesOnHold = _.map(itinerariesOnHold, 'mode');
      return {
        itinerariesOnHold,
        travelTypesOnHold: _.uniq(itinerariesModesOnHold),
        travelTypesToBook: _.difference(_.map(itineraries, 'mode'), itinerariesModesOnHold),
      };
    },
    [trip],
  );

  const formatHoldButtonText = () => {
    if (!canHold) return false;
    const { travelTypesToBook, travelTypesOnHold } = getTravelModesForHoldButton();
    return travelTypesOnHold.length
      ? formatBookButtonTextByModes(travelTypesOnHold, travelTypesToBook) : false;
  };

  const hasMenuItems = canSendEmailQuote || canRequestAdvisor || (trip && formatHoldButtonText());

  const handleBookDropdownClick = () => {
    setIsDropdownOpen((t) => !t);
    track('tripConfiguration.handleBookDropdownClick');
  };

  const handleBookDropdownClose = () => {
    setIsDropdownOpen(false);
    track('tripConfiguration.handleBookDropdownClose');
  };

  const handleEmailQuoteClick = () => {
    setOpenEmailInput(!doOpenEmailInput);
    track('tripConfiguration.handleEmailQuoteClick');
  };

  const handleContactAdvisorClick = () => {
    setOpenContactAdvInput(!openContactAdvInput);
    track('tripConfiguration.handleContactAdvisorClick');
  };

  // Loop through each flight segment and find if any flight has lower cabin than preferred.
  const checkIfAnyFlightSegmentHasLowerCabinThanPreferred = (flightItinerary) => {
    const highestRankedCabin = getHighestRankedCabinFlightItinerary(trip.flightItinerary);
    const preferredCabins = getPreferredCabins(searchObj);

    const result = flightItinerary.flightSegments.some(
      (segment, index) => segment.flights.some(
        (flight) => {
          const preferredCabin = getPreferredCabinByFlightSegmentIndex(preferredCabins, index);

          return checkDoFlightHaveCabinDowngrade(
            preferredCabin, highestRankedCabin, flight.originalCabinClass,
          );
        },
      ),
    );

    return result;
  };

  // Determine whether to show cabin downgrade alert modal or not.
  const checkDoShowCabinDowngradeAlert = () => {
    // eslint-disable-next-line max-len
    if (!trip || !trip.flightItinerary || Object.keys(trip.flightItinerary).length === 0) return false;

    return checkIfAnyFlightSegmentHasLowerCabinThanPreferred(trip.flightItinerary);
  };

  const handleBookClick = useCallback((updatedBookingAction,
    customerEmail, customerPhoneNumber, doIncludeCCWithQuote,
    doAllowSelfBookingTripQuote, messageToAdvisor, tokenizedCardMap, tokenizeFunc, isFormValid) => {
    let newTrip = null;
    if (onBookClick) {
      const addedTrip = onBookClick();
      if (!addedTrip) return;
      newTrip = { ...addedTrip };
    } else newTrip = { ...(trip || {}) };

    if ((updatedBookingAction === BookingAction.SEND_QUOTE
      || updatedBookingAction === BookingAction.ADVICE)
      && saveSendTripEmailStatus === SearchStatus.SEARCHING) { return; }
    if (saveTripStatus === SearchStatus.SEARCHING) { return; }

    if (updatedBookingAction === BookingAction.HOLD) {
      const { itinerariesOnHold } = getTravelModesForHoldButton();
      itinerariesOnHold.forEach((it) => {
        dispatch(setItineraryOnHold({
          tripId,
          itineraryMode: it.mode,
          itineraryId: it.id || 0,
        }));
      });
    }

    if (trip && trip.cruiseItinerary) {
      const cabins = [...newTrip.cruiseItinerary.cabins];
      cabins.forEach((cabin, index) => {
        if (!cabin) return;
        const rateRuleKey = getRateOptionRulesKey({
          cruiseItinerary: trip.cruiseItinerary,
          roomIndex: cabin.roomIndex,
        });
        if (rateRuleKey && allRateOptionRules[rateRuleKey]) {
          cabins[index] = {
            ...cabins[index],
            selectedRateOptionRules: allRateOptionRules[rateRuleKey],
            pricing: pricing.find((p) => p.roomIndex === cabin.roomIndex),
          };
        }
      });

      newTrip.cruiseItinerary = {
        ...newTrip.cruiseItinerary,
        cabins,
      };
    }

    const request = {
      tripId,
      trip: {
        ...newTrip,
        rentalCarItineraries: newTrip.rentalCarItineraries
          ? Object.values(newTrip.rentalCarItineraries)
          : [],
        hotelItineraries: newTrip.hotelItineraries
          ? Object.values(newTrip.hotelItineraries)
          : [],
        resultsPageUrl: window.location.href,
      },
      tripRoomSearchInput,
    };

    if (trip) {
      request.trip = {
        ...(trip || {}),
        ...request.trip,
        rentalCarItineraries: trip && trip.rentalCarItineraries
          ? Object.values(trip.rentalCarItineraries)
          : [],
        hotelItineraries: trip && trip.hotelItineraries
          ? Object.values(trip.hotelItineraries)
          : [],
        resultsPageUrl: window.location.href,
      };
    }

    request.tripStatus = updatedBookingAction;
    if (updatedBookingAction === BookingAction.SEND_QUOTE) {
      if (customerEmail) {
        request.customerEmail = customerEmail;
        dispatch(saveSendTripEmailRequest({
          ...request,
          doIncludeCC: doIncludeCCWithQuote,
          doAllowSelfBookingTripQuote,
          tokenizedCardMap,
          tokenizeFunc,
          isFormValid,
        }));
        track('tripConfiguration.sendQuote', {
          email: request.customerEmail,
        });
      }
    } else if (updatedBookingAction === BookingAction.ADVICE) {
      if (customerEmail && customerPhoneNumber
        && customerEmail.length && customerPhoneNumber.length) {
        request.customerEmail = customerEmail;
        request.customerPhoneNumber = customerPhoneNumber;
        dispatch(saveSendTripEmailRequest(request));
        track('tripConfiguration.sendAdvice', {
          email: request.customerEmail,
        });
      }
    } else if (updatedBookingAction === BookingAction.REQUEST_TO_BOOK) {
      request.customerEmail = customerEmail;
      dispatch(saveSendTripEmailRequest({
        ...request,
        messageToAdvisor,
      }));
      track('tripConfiguration.requestToBook', {
        email: request.customerEmail,
      });
    } else {
      dispatch(saveTripRequest(request));
    }

    track('tripConfiguration.handleBookClick', {
      updatedBookingAction, tripId,
    });
  }, [onBookClick, trip, saveSendTripEmailStatus,
    saveTripStatus, tripId, tripRoomSearchInput,
    track, getTravelModesForHoldButton, dispatch, allRateOptionRules, pricing]);

  const handleRequestToBook = (msgToAdvisor, clientEmail) => {
    handleBookClick(BookingAction.REQUEST_TO_BOOK, clientEmail, null, null, null, msgToAdvisor);
  };

  const handleButtonClick = (event, action) => {
    if (menuItems.indexOf(UserRolePermissions.canRequestAdvisor) > -1
      && menuItems.indexOf(UserRolePermissions.canBook) === -1
      && menuItems.indexOf(UserRolePermissions.canHold) === -1) {
      handleBookDropdownClick(event);
      setOpenContactAdvInput(true);
      return;
    }

    if (policyStatus) {
      const {
        modalContent, actions,
      } = policyStatus.policyStatus;
      if (actions && actions.includes(TravelPolicyActions.RequiresApproval)) {
        setTravelPolicyModal({
          isOpen: true,
          okButtonLabel: 'Request to Book',
          onConfirmClick: handleRequestToBook,
          action: TravelPolicyActions.RequiresApproval,
        });
        return;
      }
      if (actions && actions.includes(TravelPolicyActions.ShowMessageAndAllowBook)) {
        setTravelPolicyModal({
          isOpen: true,
          content: modalContent,
          okButtonLabel: 'Continue',
          onConfirmClick: handleBookClick,
          action: TravelPolicyActions.ShowMessageAndAllowBook,
        });
        return;
      }
    }

    const doShowCabinDowngradeAlertModal = checkDoShowCabinDowngradeAlert();
    if (doShowCabinDowngradeAlertModal) setDoShowMixedCabinWarning(true);
    else handleBookClick(action);
  };

  const bookButton = (
    <button
      className={clsx('w-full text-center text-xs text-white font-bold border-0 normal-case px-2 text-center', {
        [buttonColorCssClass]: !((policyStatus
          && policyStatus.buttonState && policyStatus.buttonState.disabled)
          || disabled),
        'bg-gray-600': ((policyStatus
            && policyStatus.buttonState && policyStatus.buttonState.disabled)
            || disabled),
        'rounded-l-2xl': hasMenuItems,
        'py-2': !hasMenuItems,
        'rounded-2xl': isBrochurePage || !hasMenuItems,
      })}
      disabled={saveTripStatus === SearchStatus.SEARCHING || (policyStatus
          && policyStatus.buttonState && policyStatus.buttonState.disabled) || disabled}
      onClick={(event) => handleButtonClick(event, BookingAction.BOOK)}
      type="button"
    >
      {menuItems.indexOf(UserRolePermissions.canRequestAdvisor) > -1
        && menuItems.indexOf(UserRolePermissions.canBook) === -1
        && menuItems.indexOf(UserRolePermissions.canHold) === -1
        ? (
          <>
            Contact advisor
            {' '}
            <ArrowDropDown />
          </>
        )
        : (
          <div className="flex items-center justify-center">
            <div>{formatBookButtonTextByTrip(trip, travelAgency, policyStatus)}</div>
            {(saveTripStatus === SearchStatus.SEARCHING
              || (saveTripStatus === SearchStatus.DONE && trip && trip.tripCode))
              && <CircularProgress color="inherit" className="ml-2" size={14} />}
          </div>
        )}
    </button>
  );
  if (isBrochurePage) {
    return (
      <div
        className="col-span-3 pt-2 relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Tooltip arrow open={isHovered && disabled} title={disableBookingTooltip || 'Booking is currently unavailable.'} classes={classes} placement="top" enterTouchDelay={0}>
          <ButtonGroup classes={buttonGroupClasses} component="div" aria-label="outlined primary button group" disableElevation disableFocusRipple>
            {bookButton}
          </ButtonGroup>
        </Tooltip>
      </div>
    );
  }
  return (
    <div className="col-span-3 pt-2 relative">
      {hasMenuItems ? (
        <Tooltip
          arrow
          title={(policyStatus && policyStatus.policyStatus && policyStatus.policyStatus.tooltip) || ''}
          open={Boolean(policyStatus && policyStatus.policyStatus
            && !_.isEmpty(policyStatus.policyStatus.tooltip)
            && !_.isNil(policyStatus.policyStatus.tooltip))}
          placement="top"
        >
          <ButtonGroup
            classes={buttonGroupClasses}
            component="div"
            aria-label="outlined primary button group"
            disableElevation
            disableFocusRipple
          >
            {bookButton}
            <button
              type="button"
              className={clsx('text-center text-xs text-white font-bold border-white border-0 border-l rounded-r-2xl', {
                [buttonColorCssClass]: !(policyStatus
                  && policyStatus.buttonState && policyStatus.buttonState.disabled),
                'bg-gray-600': (policyStatus
                  && policyStatus.buttonState && policyStatus.buttonState.disabled),
              })}
              onClick={handleBookDropdownClick}
            >
              <ArrowDropDown />
            </button>
          </ButtonGroup>
        </Tooltip>
      ) : bookButton}
      <Popover
        open={isDropdownOpen}
        close={handleBookDropdownClose}
        widthClass="w-fit min-w-80"
        marginClass="mt-0"
      >
        <>
          {canSendEmailQuote && (
          <MenuItem component="div" onClick={handleEmailQuoteClick}>
            <ListItemIcon>
              <EmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Email quote" classes={{ primary: listItemTextClasses.listItemText }} />

            {saveSendTripEmailStatus === SearchStatus.DONE && !saveSendTripEmailError
              && <DoneAllIcon fontSize="small" className="text-green-500" />}
            {doOpenEmailInput ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </MenuItem>
          )}
          {canSendEmailQuote && (
          <Collapse in={doOpenEmailInput} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem>
                <EmailTripQuoteForm
                  saveSendTripEmailStatus={saveSendTripEmailStatus}
                  setOpenEmailInput={(val) => setOpenEmailInput(val)}
                  onBookClick={handleBookClick}
                  saveSendTripEmailError={saveSendTripEmailError}
                  tripId={tripId}
                />
              </ListItem>
            </List>
          </Collapse>
          )}
          {canRequestAdvisor && (
          <MenuItem component="div" onClick={handleContactAdvisorClick}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Contact advisor" classes={{ primary: listItemTextClasses.listItemText }} />
            {openContactAdvInput ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </MenuItem>
          )}
          {canRequestAdvisor && (
          <Collapse in={openContactAdvInput} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem component="div">
                <ContactAdvisorForm
                  saveSendTripEmailStatus={saveSendTripEmailStatus}
                  setOpenContactAdvInput={menuItems.length === 1
                    && menuItems[0] === UserRolePermissions.canRequestAdvisor
                    ? handleBookDropdownClick : setOpenContactAdvInput}
                  onBookClick={handleBookClick}
                  error={saveSendTripEmailError}
                />
              </ListItem>
            </List>
          </Collapse>
          )}
          {trip && formatHoldButtonText() && (
          <MenuItem component="div" onClick={() => handleBookClick(BookingAction.HOLD)}>
            <ListItemIcon>
              <AccessAlarmIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={formatHoldButtonText()}
              classes={{ ...listItemClasses, primary: listItemTextClasses.listItemText }}
            />
            {(saveTripStatus === SearchStatus.SEARCHING
              || (saveTripStatus === SearchStatus.DONE && trip && trip.tripCode))
              && <CircularProgress color="secondary" className="ml-3" size={15} />}
          </MenuItem>
          )}
        </>
      </Popover>
      {error ? <div className="text-red-600 text-sm">{error}</div> : null}
      {!doShowMixedCabinWarning ? <></>
        : (
          <LowerCabinAlertModal
            setDoShowMixedCabinWarning={setDoShowMixedCabinWarning}
            onConfirmClick={handleBookClick}
          />
        )}
      {travelPolicyModal.isOpen && (
        <TravelPolicyConfirmationModal
          message={travelPolicyModal.content}
          onToggleModal={handleCloseTravelPolicyModal}
          onConfirmClick={travelPolicyModal.onConfirmClick}
          tripId={tripId}
          okButtonLabel={travelPolicyModal.okButtonLabel}
          action={travelPolicyModal.action}
        />
      )}
    </div>
  );
};

TripBookButton.propTypes = {
  trip: PropTypes.shape().isRequired,
  pricing: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape()), PropTypes.shape({})]).isRequired,
  saveTripStatus: PropTypes.number,
  saveSendTripEmailStatus: PropTypes.number,
  error: PropTypes.string,
  saveSendTripEmailError: PropTypes.string,
  tripId: PropTypes.string.isRequired,
  buttonGroupStyles: PropTypes.shape({}),
  buttonColorCssClass: PropTypes.string,
  onBookClick: PropTypes.func,
  disabled: PropTypes.bool,
  disableBookingTooltip: PropTypes.string,
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
};

TripBookButton.defaultProps = {
  error: undefined,
  saveSendTripEmailError: undefined,
  saveTripStatus: undefined,
  saveSendTripEmailStatus: undefined,
  buttonGroupStyles: { paddingTop: '0.5rem', paddingBottom: '0.5rem' },
  buttonColorCssClass: 'bg-green-600',
  onBookClick: undefined,
  disabled: false,
  disableBookingTooltip: undefined,
  tripRoomSearchInput: undefined,
};
export default TripBookButton;
